import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, API, wsUrl, fetchIceServers } from "@/lib/api";
import { fmtTime } from "@/lib/datetime";
import { useAuth } from "@/lib/auth.jsx";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Camera, CameraOff, Mic, MicOff, PhoneOff, Send, ImagePlus, FileDown,
  Copy, MessageSquare, ArrowLeft, Wifi, WifiOff, Users, Loader2, Sparkles, Brain,
  Flashlight, FlashlightOff, Info, ChevronDown, ChevronUp, ExternalLink, ShieldAlert,
} from "lucide-react";
import { toast } from "sonner";
import { createPeer, handleSignal } from "@/lib/webrtc";
import { AiNotesPanel } from "@/components/call/AiNotesPanel";
import { AiNarrativePanel } from "@/components/call/AiNarrativePanel";
import { debug } from "@/lib/debug";

export default function CallRoomPerito() {
  const { id } = useParams();
  const nav = useNavigate();
  const { token } = useAuth();

  const [siniestro, setSiniestro] = useState(null);
  const [peerConnected, setPeerConnected] = useState(false);
  const [aseguradoPresent, setAseguradoPresent] = useState(false);
  const [camOn, setCamOn] = useState(false);
  const [hasCamera, setHasCamera] = useState(true);
  const [micOn, setMicOn] = useState(true);
  const [insuredTorchSupported, setInsuredTorchSupported] = useState(false);
  const [insuredTorchOn, setInsuredTorchOn] = useState(false);
  const [chat, setChat] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [transcript, setTranscript] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [capturing, setCapturing] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  // Incidente de permisos del asegurado (cámara/micrófono/ubicación denegado)
  // Persistente hasta que el perito lo cierre o conecte la peer.
  const [permissionIssue, setPermissionIssue] = useState(null);
  const [aiNotes, setAiNotes] = useState(null);
  const [aiNotesAt, setAiNotesAt] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiNarrative, setAiNarrative] = useState("");
  const [aiNarrativeEdited, setAiNarrativeEdited] = useState(false);
  // Estado fino de la conexión con el asegurado para la UI.
  // "idle" → aún sin asegurado; "connecting" → ICE checking; "reconnecting"
  // → glitch transitorio; "connected" → todo OK.
  const [connState, setConnState] = useState("idle");
  // Briefing del caso: visible por defecto al entrar a la sala para que el
  // perito sepa de qué va antes de hablar con el asegurado. Se puede colapsar.
  const [briefingOpen, setBriefingOpen] = useState(true);
  // Error fatal al intentar acceder a cámara/micrófono. Causa típica:
  // Avizor (u otro ERP) embebe esta página en un <iframe> SIN el atributo
  // `allow="camera; microphone; ..."`, por lo que la *Permissions Policy* del
  // navegador bloquea getUserMedia. Mostramos al perito un mensaje explicativo
  // con un botón para abrir la sala en pestaña propia (fuera del iframe).
  const [mediaError, setMediaError] = useState(null); // { name, message, inIframe }

  const transcriptCharsRef = useRef(0);     // último volumen de transcripción enviado al LLM
  const aiPollTimerRef = useRef(null);

  const remoteVideoRef = useRef(null);
  const localVideoRef = useRef(null);
  const wsRef = useRef(null);
  const pcRef = useRef(null);
  const localStreamRef = useRef(null);
  const chatEndRef = useRef(null);
  const recordingActiveRef = useRef(false);
  const wsReconnectAttemptsRef = useRef(0);
  const remoteTrackReceivedRef = useRef(false);   // true tras el primer ontrack del asegurado
  const noTrackWatchdogRef = useRef(null);        // timer que pide regenerar offer si no llegan tracks
  const noTrackRetriesRef = useRef(0);            // nº de reintentos ya lanzados
  const buildPeerRef = useRef(null);              // función para recrear el pc del perito desde cero (equivalente a refresh manual)

  // Transcripción del audio del perito (envía chunks al backend con role=perito)
  const startPeritoTranscription = (stream) => {
    if (!window.MediaRecorder || !siniestro) return;
    recordingActiveRef.current = true;
    const audioStream = new MediaStream(stream.getAudioTracks());
    let audioCtx = null, analyser = null, dataArr = null;
    try {
      audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      const source = audioCtx.createMediaStreamSource(audioStream);
      analyser = audioCtx.createAnalyser();
      analyser.fftSize = 2048;
      source.connect(analyser);
      dataArr = new Uint8Array(analyser.frequencyBinCount);
    } catch (e) { console.warn("[perito-stt] AudioContext failed", e); }
    const measureRms = () => {
      if (!analyser) return 1;
      analyser.getByteTimeDomainData(dataArr);
      let sum = 0;
      for (let i = 0; i < dataArr.length; i++) {
        const v = (dataArr[i] - 128) / 128; sum += v * v;
      }
      return Math.sqrt(sum / dataArr.length);
    };
    const MIN_CHUNK_MS = 4000;
    const MAX_CHUNK_MS = 12000;
    const SILENCE_FLUSH_MS = 700;
    const RMS_THRESHOLD = 0.012;
    const MIN_VOICED_MS = 250;
    const tick = () => {
      if (!recordingActiveRef.current) return;
      const mime = "audio/webm";
      let maxRms = 0;
      let voicedMs = 0;
      let silenceMs = 0;
      let elapsedMs = 0;
      let rmsInterval = null;
      let chunkTimeoutId = null;
      let stopped = false;
      const stopRecorder = (rec) => {
        if (stopped) return;
        stopped = true;
        if (rmsInterval) clearInterval(rmsInterval);
        if (chunkTimeoutId) clearTimeout(chunkTimeoutId);
        try { rec.state !== "inactive" && rec.stop(); } catch (err) { console.warn("[perito-stt] stop", err); }
      };
      try {
        const r = new MediaRecorder(audioStream, { mimeType: mime });
        const chunks = [];
        r.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunks.push(e.data); };
        r.onstop = async () => {
          try {
            const blob = new Blob(chunks, { type: mime });
            debug(`[perito-stt] chunk size=${blob.size}B maxRms=${maxRms.toFixed(4)} voiced=${voicedMs}ms elapsed=${elapsedMs}ms micOn=${micOn}`);
            if (blob.size > 2500 && maxRms >= RMS_THRESHOLD && voicedMs >= MIN_VOICED_MS) {
              const fd = new FormData();
              fd.append("audio", blob, "chunk.webm");
              fd.append("role", "perito");
              await fetch(`${API}/session/${siniestro.session_token}/transcribe`, { method: "POST", body: fd });
            }
          } catch (err) { console.warn("[perito-stt] upload error", err); }
          if (recordingActiveRef.current) tick();
        };
        r.start();
        rmsInterval = setInterval(() => {
          const rms = measureRms();
          if (rms > maxRms) maxRms = rms;
          elapsedMs += 100;
          if (rms >= RMS_THRESHOLD) {
            voicedMs += 100;
            silenceMs = 0;
          } else {
            silenceMs += 100;
          }
          if (
            elapsedMs >= MIN_CHUNK_MS &&
            voicedMs >= MIN_VOICED_MS &&
            silenceMs >= SILENCE_FLUSH_MS
          ) {
            stopRecorder(r);
          }
        }, 100);
        chunkTimeoutId = setTimeout(() => stopRecorder(r), MAX_CHUNK_MS);
      } catch (e) { console.warn("[perito-stt] MediaRecorder error", e); }
    };
    tick();
  };

  // Load siniestro
  useEffect(() => {
    api.get(`/siniestros/${id}`).then(r => {
      const sin = r.data.siniestro;
      // Los expedientes telemáticos NO tienen sala de videollamada — redirigimos al detalle.
      if ((sin?.kind || "videoperitacion") === "telematico") {
        toast.info("Este expediente es telemático: no requiere videollamada.");
        nav(`/siniestros/${id}`);
        return;
      }
      setSiniestro(sin);
      setChat(r.data.chat || []);
      setTranscript(r.data.transcript || []);
      setPhotos((r.data.photos || []).map(p => ({ ...p, url: `${API}/photos/${p.id}` })));
    }).catch(() => toast.error("No se pudo cargar la sala"));
    // Carga notas IA ya guardadas en BD para mostrarlas al entrar (siniestros largos / reentradas)
    api.get(`/siniestros/${id}/ai-notes`).then(r => {
      if (r.data?.notes && Object.keys(r.data.notes).length) setAiNotes(r.data.notes);
      if (r.data?.updated_at) setAiNotesAt(r.data.updated_at);
      if (r.data?.narrative) setAiNarrative(r.data.narrative);
      setAiNarrativeEdited(!!r.data?.narrative_edited);
      transcriptCharsRef.current = r.data?.transcript_chars || 0;
    }).catch(() => {});
  }, [id]);

  // Asistente IA: refresca las notas extraídas a partir de la transcripción acumulada.
  // - Se dispara cada 30s mientras haya nueva transcripción significativa (≥120 chars nuevos).
  // - También se puede forzar manualmente con el botón "Refrescar".
  const refreshAiNotes = async (manual = false) => {
    if (aiLoading) return;
    setAiLoading(true);
    try {
      const r = await api.post(`/siniestros/${id}/ai-notes`);
      setAiNotes(r.data?.notes || null);
      setAiNotesAt(r.data?.updated_at || new Date().toISOString());
      if (typeof r.data?.narrative === "string") setAiNarrative(r.data.narrative);
      setAiNarrativeEdited(!!r.data?.narrative_edited);
      transcriptCharsRef.current = r.data?.transcript_chars || 0;
      if (manual) toast.success("Notas IA actualizadas");
    } catch (e) {
      console.warn("[ai-notes] error", e);
      if (manual) toast.error("No se pudieron actualizar las notas IA");
    } finally {
      setAiLoading(false);
    }
  };

  useEffect(() => {
    if (!siniestro) return;
    aiPollTimerRef.current = setInterval(() => {
      const totalChars = transcript.reduce((acc, t) => acc + (t?.text?.length || 0), 0);
      if (totalChars - transcriptCharsRef.current >= 120) refreshAiNotes(false);
    }, 30000);
    return () => clearInterval(aiPollTimerRef.current);
    // Sólo depende del siniestro; el polling lee transcript via closure refrescado cada tick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siniestro, transcript]);

  // Setup media + WS + peer connection
  useEffect(() => {
    if (!siniestro) return;
    let stopped = false;

    (async () => {
      // ICE servers (Twilio TURN if available)
      const iceServers = await fetchIceServers();
      // Detectamos si estamos embebidos en un iframe (típico: Avizor / ERP).
      // Lo guardamos para mostrar mensajes específicos si getUserMedia falla:
      // los iframes cross-origin necesitan `allow="camera; microphone"` en
      // el elemento padre, si no, el navegador bloquea el acceso silenciosamente.
      let inIframe = false;
      try { inIframe = window.self !== window.top; } catch (_) { inIframe = true; }
      // Media local del perito. La cámara es OPCIONAL: si el ordenador no tiene
      // cámara, hacemos fallback a SOLO audio. La videoperitación se realiza
      // con la cámara del asegurado, no con la del perito; la cámara del perito
      // arranca apagada por defecto y solo se enciende si él lo decide.
      let stream = null;
      let hasVideoTrack = false;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
        hasVideoTrack = stream.getVideoTracks().length > 0;
      } catch (errBoth) {
        console.warn("[perito] getUserMedia audio+video falló, probando solo audio:", errBoth?.name || errBoth);
        try {
          stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
          hasVideoTrack = false;
          toast.info("Sin cámara detectada. Continuamos solo con audio (la cámara del perito es opcional).");
        } catch (errAudio) {
          console.error("[perito] getUserMedia audio falló:", errAudio?.name || errAudio, errAudio);
          // Guardamos detalle del error para mostrar al perito un mensaje específico
          // (con botón "Abrir en pestaña nueva" si estamos en iframe).
          setMediaError({
            name: errAudio?.name || "MediaError",
            message: errAudio?.message || String(errAudio || ""),
            inIframe,
          });
          if (inIframe && (errAudio?.name === "NotAllowedError" || /permission/i.test(errAudio?.message || ""))) {
            toast.error("Avizor no ha permitido cámara/micrófono. Abre la sala en pestaña nueva.", { duration: 12000 });
          } else {
            toast.error("No se pudo acceder al micrófono. Revisa los permisos del navegador.");
          }
          return;
        }
      }
      if (stopped) { stream.getTracks().forEach(t => t.stop()); return; }
      // Si trajimos vídeo, dejarlo deshabilitado de inicio (decide el perito).
      // Si no, ocultamos el botón "Cámara" de los controles a través de hasCamera.
      if (hasVideoTrack) {
        stream.getVideoTracks().forEach(t => (t.enabled = false));
      }
      setHasCamera(hasVideoTrack);
      localStreamRef.current = stream;
      if (localVideoRef.current) localVideoRef.current.srcObject = stream;

      // Extraemos la creación del RTCPeerConnection a una función para poder
      // recrearla desde el watchdog cuando el pc queda en estado corrupto (mismo
      // efecto que hacía el refresh manual que el usuario confirmó que funciona).
      const buildPeer = () => {
        const pc = createPeer({
          iceServers,
          sendSignal: (payload) => {
            const w = wsRef.current;
            if (w && w.readyState === WebSocket.OPEN) {
              w.send(JSON.stringify({ type: "signal", payload }));
            }
          },
          onTrack: (s) => {
            debug("[perito] remote track received");
            remoteTrackReceivedRef.current = true;
            noTrackRetriesRef.current = 0;
            if (noTrackWatchdogRef.current) {
              clearTimeout(noTrackWatchdogRef.current);
              noTrackWatchdogRef.current = null;
            }
            if (remoteVideoRef.current) {
              remoteVideoRef.current.srcObject = s;
              remoteVideoRef.current.play().catch(() => { /* autoplay */ });
            }
          },
          onState: (st, ice) => {
            if (st === "connected" || ice === "connected" || ice === "completed") {
              setPeerConnected(true);
              setConnState("connected");
            } else if (ice === "checking" || st === "connecting") {
              setConnState("connecting");
            } else if (ice === "disconnected" || st === "disconnected") {
              setPeerConnected(false);
              setConnState("reconnecting");
            } else if (ice === "failed" || st === "failed") {
              setPeerConnected(false);
              setConnState("reconnecting");
            }
          },
        });
        stream.getTracks().forEach(t => pc.addTrack(t, stream));
        return pc;
      };
      buildPeerRef.current = buildPeer;

      const pc = buildPeer();
      pcRef.current = pc;

      // Encapsulamos la conexión WS en una función para poder reconectarla
      // automáticamente con backoff si la red del perito tiene un glitch.
      const connectWs = () => {
        const ws = new WebSocket(wsUrl(siniestro.session_token, "perito", token()));
        wsRef.current = ws;
        // Keepalive ping cada 25 s: el servidor responde con pong. Sin esto,
        // Cloudflare y otros proxies WAF cierran el WS por inactividad (~100 s)
        // mientras el perito espera al asegurado, lo que disparaba el bucle
        // de reconexión hasta el toast "No se pudo restablecer la conexión".
        let pingTimer = null;
        ws.onopen = async () => {
          debug("[perito] WS open");
          wsReconnectAttemptsRef.current = 0;
          startPeritoTranscription(stream);
          if (pingTimer) clearInterval(pingTimer);
          pingTimer = setInterval(() => {
            if (ws.readyState === WebSocket.OPEN) {
              try { ws.send(JSON.stringify({ type: "ping" })); } catch (_) { /* ignore */ }
            }
          }, 25000);
          // Sincronizar galería con el backend en cada apertura del WS.
          // Cubre 2 escenarios críticos:
          //   1) El perito entra tarde y el asegurado ya había subido fotos.
          //   2) El WS se cayó durante la sesión y volvió a conectar → los
          //      broadcasts de foto que ocurrieron durante el corte se perdieron.
          try {
            const r = await api.get(`/siniestros/${id}/photos-list`);
            const items = r.data?.items || [];
            const withUrls = items.map((it) => ({
              ...it,
              url: `${process.env.REACT_APP_BACKEND_URL}${it.url}`,
            }));
            setPhotos((prev) => {
              // Merge: mantener las fotos que ya estén y no estén en el snapshot
              // (poco probable), pero preferir el snapshot como fuente de verdad.
              const byId = new Map();
              withUrls.forEach((p) => byId.set(p.id, p));
              prev.forEach((p) => { if (!byId.has(p.id)) byId.set(p.id, p); });
              return Array.from(byId.values()).sort(
                (a, b) => new Date(a.captured_at || 0) - new Date(b.captured_at || 0)
              );
            });
          } catch (e) {
            debug("[perito] photos-list sync failed:", e?.message);
          }
        };
        ws.onmessage = async (ev) => {
          let msg; try { msg = JSON.parse(ev.data); } catch { return; }
          debug("[perito] WS msg:", msg.type, msg.role || "");

          // Watchdog reusable: si el asegurado está presente pero no llega
          // ningún track de vídeo (signaling OK pero media KO — pantalla
          // negra), pedimos al asegurado regenerar la offer con iceRestart.
          // Reintenta hasta 3 veces cada 8 s. Se re-arma en `peers` y en
          // `peer_joined asegurado` para cubrir ambos órdenes de entrada.
          const armPeritoWatchdog = () => {
            if (noTrackWatchdogRef.current) clearTimeout(noTrackWatchdogRef.current);
            noTrackWatchdogRef.current = setTimeout(() => {
              if (!remoteTrackReceivedRef.current && noTrackRetriesRef.current < 3) {
                noTrackRetriesRef.current += 1;
                const w = wsRef.current;

                // Intento 1 (8s): pedir al asegurado que regenere la offer.
                // Intento 2+ (16s, 24s): además de pedir la offer, RECREAR el
                // pc del perito desde cero (equivalente al refresh manual que
                // sabemos que funciona). El pc viejo puede estar en estado
                // corrupto (transceivers desalineados, ICE stuck) y solo un
                // pc fresco negocia limpio con el asegurado.
                if (noTrackRetriesRef.current >= 2 && buildPeerRef.current) {
                  debug(`[perito] watchdog: intento ${noTrackRetriesRef.current}/3 → RECREAR pc del perito y pedir offer nueva`);
                  try { pcRef.current?.close(); } catch (_) { /* ignore */ }
                  try {
                    pcRef.current = buildPeerRef.current();
                    remoteTrackReceivedRef.current = false;
                  } catch (e) {
                    debug("[perito] fallo al recrear pc:", e);
                  }
                } else {
                  debug(`[perito] watchdog: asegurado presente pero sin track tras 8s (intento ${noTrackRetriesRef.current}/3) → request_offer_regen`);
                }

                if (w && w.readyState === WebSocket.OPEN) {
                  try { w.send(JSON.stringify({ type: "request_offer_regen" })); } catch (_) { /* ignore */ }
                }
                armPeritoWatchdog();
              } else if (!remoteTrackReceivedRef.current) {
                debug("[perito] watchdog: 3 reintentos sin track. Recarga automática de la sala.");
                toast.info(
                  "No se recibe vídeo del asegurado. Recuperando la conexión automáticamente…",
                  { duration: 4000 }
                );
                // Última bala: recargar la sala (equivalente al refresh manual
                // que YA sabemos que resuelve el problema).
                setTimeout(() => {
                  try { window.location.reload(); } catch (_) { /* ignore */ }
                }, 1200);
              }
            }, 8000);
          };

          if (msg.type === "peers") {
            const insuredPresent = msg.present.includes("asegurado");
            setAseguradoPresent(insuredPresent);
            // Si al abrir el WS el asegurado YA estaba en la sala (perito
            // recargó, entró tarde…) arrancamos también el watchdog.
            if (insuredPresent && !remoteTrackReceivedRef.current) {
              armPeritoWatchdog();
            }
          } else if (msg.type === "peer_joined" && msg.role === "asegurado") {
            setAseguradoPresent(true);
            toast.success("El asegurado se ha conectado");
            remoteTrackReceivedRef.current = false;
            noTrackRetriesRef.current = 0;
            armPeritoWatchdog();
          } else if (msg.type === "peer_left" && msg.role === "asegurado") {
            setAseguradoPresent(false);
            setInsuredTorchSupported(false);
            setInsuredTorchOn(false);
            // Limpiar watchdog y flag de tracks — cuando vuelva a entrar
            // el asegurado, arrancaremos un ciclo nuevo desde cero.
            if (noTrackWatchdogRef.current) {
              clearTimeout(noTrackWatchdogRef.current);
              noTrackWatchdogRef.current = null;
            }
            remoteTrackReceivedRef.current = false;
            noTrackRetriesRef.current = 0;
          } else if (msg.type === "torch_state") {
            setInsuredTorchSupported(!!msg.supported);
            setInsuredTorchOn(!!msg.on);
          } else if (msg.type === "signal") {
            await handleSignal(pc, msg.payload, {
              sendSignal: (p) => {
                const w = wsRef.current;
                if (w && w.readyState === WebSocket.OPEN) {
                  w.send(JSON.stringify({ type: "signal", payload: p }));
                }
              },
            });
          } else if (msg.type === "chat") {
            setChat((c) => [...c, msg]);
            setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
          } else if (msg.type === "transcript") {
            setTranscript((t) => [...t, msg]);
          } else if (msg.type === "photo") {
            setPhotos((p) => [...p, { ...msg, url: `${process.env.REACT_APP_BACKEND_URL}${msg.url}` }]);
            setCapturing(false);
            toast.success("Fotografía capturada");
          } else if (msg.type === "asegurado_permission_issue") {
            setPermissionIssue({
              ts: msg.ts || new Date().toISOString(),
              camera_denied: !!msg.camera_denied,
              microphone_denied: !!msg.microphone_denied,
              geolocation_denied: !!msg.geolocation_denied,
              kind: msg.kind || "",
              message: msg.message || "",
              insured_phone: msg.insured_phone || "",
            });
            const parts = [];
            if (msg.camera_denied) parts.push("cámara");
            if (msg.microphone_denied) parts.push("micrófono");
            if (msg.geolocation_denied) parts.push("ubicación");
            const phoneHint = msg.insured_phone ? ` · Llámale al ${msg.insured_phone}` : "";
            toast.error(
              `El asegurado no puede activar: ${parts.join(", ") || "permisos"}.${phoneHint}`,
              { duration: 12000 }
            );
          }
        };
        ws.onerror = (e) => console.warn("[perito] WS error event:", e?.type, "readyState=", ws.readyState);
        ws.onclose = (ev) => {
          // Log SIEMPRE el código y razón del cierre (no usamos debug() porque
          // necesitamos verlo en producción para diagnosticar rechazos 4xxx).
          console.warn("[perito] WS closed", { code: ev.code, reason: ev.reason, wasClean: ev.wasClean });
          if (pingTimer) { clearInterval(pingTimer); pingTimer = null; }
          setPeerConnected(false);
          // Cierre normal (1000) o aplicativo (4xxx) → no reconectar.
          // Para los 4xxx mostramos al perito el motivo concreto (en vez del
          // genérico "Reconectando" que oculta lo que está pasando realmente).
          if (ev.code >= 4000 && ev.code < 5000) {
            const reasonMap = {
              4001: "Rol de WebSocket inválido.",
              4003: ev.reason === "token-invalid"
                ? "Tu sesión del ERP ha caducado. Pide al ERP que genere un nuevo enlace de sala (los enlaces expiran a las 4h)."
                : ev.reason === "must-change-password"
                ? "Tu cuenta requiere cambio de contraseña obligatorio. Entra al panel principal y cámbiala."
                : ev.reason === "user-not-found"
                ? "Usuario no encontrado en el sistema."
                : ev.reason === "user-suspended"
                ? "Cuenta suspendida. Contacta con el administrador."
                : ev.reason === "not-authorized"
                ? "No tienes permiso para acceder a esta videoperitación."
                : `Acceso denegado (${ev.reason || "sin detalle"}).`,
              4004: "Esta sesión de videoperitación no existe o ya fue eliminada.",
              4010: "Esta videoperitación ya está cerrada. No es posible reabrirla.",
            };
            const userMsg = reasonMap[ev.code] || `WebSocket cerrado: ${ev.code} ${ev.reason || ""}`;
            toast.error(userMsg, { duration: 20000 });
            setConnState("idle");
            return;
          }
          if (ev.code === 1000) return;
          if (stopped) return;
          const attempts = ++wsReconnectAttemptsRef.current;
          if (attempts > 8) {
            toast.error("No se pudo restablecer la conexión con el asegurado. Recarga la página.");
            return;
          }
          const delay = Math.min(15000, 1000 * Math.pow(2, attempts - 1));
          setConnState("reconnecting");
          debug(`[perito] WS reconnect in ${delay} ms (intento ${attempts})`);
          setTimeout(connectWs, delay);
        };
      };
      connectWs();
    })();

    return () => {
      stopped = true;
      recordingActiveRef.current = false;
      if (noTrackWatchdogRef.current) {
        clearTimeout(noTrackWatchdogRef.current);
        noTrackWatchdogRef.current = null;
      }
      try { wsRef.current?.close(); } catch { /* ignore: teardown */ }
      try { pcRef.current?.close(); } catch { /* ignore: teardown */ }
      try { localStreamRef.current?.getTracks().forEach(t => t.stop()); } catch { /* ignore: teardown */ }
    };
    // eslint-disable-next-line
  }, [siniestro]);

  const sendChat = () => {
    const t = chatInput.trim();
    if (!t || !wsRef.current || wsRef.current.readyState !== 1) return;
    wsRef.current.send(JSON.stringify({ type: "chat", text: t }));
    setChatInput("");
  };

  const toggleCam = () => {
    if (!hasCamera) {
      toast("Este equipo no tiene cámara disponible.");
      return;
    }
    const next = !camOn;
    setCamOn(next);
    const s = localStreamRef.current;
    if (s) s.getVideoTracks().forEach(t => (t.enabled = next));
    wsRef.current?.send(JSON.stringify({ type: "camera", on: next }));
  };
  const toggleMic = () => {
    const next = !micOn;
    setMicOn(next);
    const s = localStreamRef.current;
    if (s) s.getAudioTracks().forEach(t => (t.enabled = next));
  };

  const toggleInsuredTorch = () => {
    if (!insuredTorchSupported) return;
    const next = !insuredTorchOn;
    // Optimistic UI: el estado real se confirmará por torch_state desde el móvil
    setInsuredTorchOn(next);
    try {
      wsRef.current?.send(JSON.stringify({ type: "torch", on: next }));
    } catch (_) { /* ignore */ }
  };

  const capture = () => {
    if (!aseguradoPresent) {
      toast.error("Esperando a que se conecte el asegurado");
      return;
    }
    setCapturing(true);
    wsRef.current?.send(JSON.stringify({ type: "request_capture" }));
    // Timeout fallback
    setTimeout(() => setCapturing(false), 8000);
  };

  const copyLink = async () => {
    const url = `${window.location.origin}/s/${siniestro.session_token}`;
    await navigator.clipboard.writeText(url);
    toast.success("Enlace copiado");
  };

  const endAndFinalize = async () => {
    if (!window.confirm("¿Finalizar la videoperitación y generar certificado?")) return;
    setFinalizing(true);
    try {
      // El backend hará broadcast end_call por WS al asegurado dentro de /finalize
      await api.post(`/siniestros/${id}/finalize`);
      toast.success("Certificado generado");
      window.open(`${API}/siniestros/${id}/certificate?token=${token()}`, "_blank");
      nav(`/siniestros/${id}`);
    } catch (e) {
      toast.error("Error finalizando");
    } finally {
      setFinalizing(false);
    }
  };

  if (!siniestro) return <div className="p-10 text-white/60">Cargando sala…</div>;

  // Si getUserMedia falló (típicamente por iframe sin `allow=...`), no tiene
  // sentido seguir con la sala: mostramos pantalla explicativa con un botón
  // que abre la sala en ventana propia, fuera del iframe — donde el
  // navegador sí pedirá los permisos de cámara y micrófono.
  if (mediaError) {
    const isPermissionDenied = mediaError.name === "NotAllowedError" || /permission/i.test(mediaError.message || "");
    const inIframe = !!mediaError.inIframe;
    const openInNewTab = () => {
      try {
        // Abrimos la URL actual sin el iframe wrapper. Usamos noopener para
        // que el ERP no controle la ventana resultante (mejor UX y seguridad).
        window.open(window.location.href, "_blank", "noopener,noreferrer");
      } catch (_) { /* ignore */ }
    };
    return (
      <div className="h-screen flex items-center justify-center bg-[#1A1A1C] text-white px-6" data-testid="media-error-screen">
        <div className="max-w-lg w-full bg-[#2A1A1A] border border-[#B43F3F] rounded-[14px] p-6 shadow-xl">
          <div className="flex items-center gap-3 mb-3">
            <ShieldAlert className="h-6 w-6 text-[#FAB28A]" />
            <div className="font-heading font-semibold text-lg">No se pudo activar tu cámara y micrófono</div>
          </div>
          {inIframe && isPermissionDenied ? (
            <>
              <p className="text-sm text-white/85 leading-relaxed">
                Esta sala está embebida en una ventana del ERP (Avizor), y el navegador no
                permite usar cámara/micrófono dentro de iframes salvo que el ERP lo declare
                explícitamente.
              </p>
              <p className="text-sm text-white/85 leading-relaxed mt-2">
                <strong>Solución inmediata:</strong> abre la sala en una pestaña nueva. Allí el navegador
                te pedirá los permisos con normalidad y la videoperitación funcionará.
              </p>
              <p className="text-xs text-white/55 mt-3">
                <strong>Solución definitiva (para el equipo de Avizor):</strong> añadir al iframe que
                embebe esta sala el atributo&nbsp;
                <code className="bg-black/40 px-1.5 py-0.5 rounded text-[11px]">{'allow="camera; microphone; display-capture; autoplay; geolocation; clipboard-read; clipboard-write"'}</code>.
              </p>
            </>
          ) : (
            <>
              <p className="text-sm text-white/85 leading-relaxed">
                El navegador ha bloqueado el acceso a la cámara o al micrófono.
                Revisa los permisos del sitio (icono del candado en la barra de direcciones)
                y vuelve a intentarlo.
              </p>
              {inIframe && (
                <p className="text-xs text-white/55 mt-3">
                  Detectamos que esta sala se ha abierto dentro de un iframe del ERP.
                  Si el problema persiste, prueba a abrirla en una pestaña nueva.
                </p>
              )}
            </>
          )}
          <div className="mt-5 flex flex-wrap items-center gap-2">
            {inIframe && (
              <Button
                onClick={openInNewTab}
                className="bg-[#D85A30] hover:bg-[#C24F29] text-white rounded-[10px]"
                data-testid="open-in-new-tab-btn"
              >
                <ExternalLink className="h-4 w-4 mr-2" /> Abrir en pestaña nueva
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => window.location.reload()}
              className="bg-transparent border-white/20 text-white hover:bg-white/10 rounded-[10px]"
              data-testid="retry-media-btn"
            >
              Reintentar
            </Button>
          </div>
          <div className="mt-4 text-[10px] font-mono text-white/40 break-all" data-testid="media-error-detail">
            {mediaError.name}{mediaError.message ? ` — ${mediaError.message}` : ""}
          </div>
        </div>
      </div>
    );
  }

  // Etiquetas y tooltip del botón de cámara (sin ternarios anidados en el JSX).
  let camBtnLabel = "Encender cámara";
  let camBtnTitle = "Encender cámara";
  if (!hasCamera) {
    camBtnLabel = "Sin cámara";
    camBtnTitle = "Sin cámara en este equipo (opcional)";
  } else if (camOn) {
    camBtnLabel = "Cámara encendida";
    camBtnTitle = "Apagar cámara";
  }

  return (
    <div className="h-screen flex flex-col bg-[#1A1A1C] text-white" data-testid="call-room-perito">
      {/* Top bar */}
      <header className="h-14 px-5 flex items-center justify-between border-b border-white/10 bg-[#1A1A1C]/90 backdrop-blur">
        <div className="flex items-center gap-3">
          <Link to={`/siniestros/${id}`} className="text-white/70 hover:text-white">
            <ArrowLeft className="h-4 w-4" />
          </Link>
          <div>
            <div className="text-[11px] text-white/55">Expediente</div>
            <div className="font-heading font-semibold text-sm">{siniestro.expediente_num} · {siniestro.asegurado_nombre}</div>
          </div>
        </div>
        <div className="flex items-center gap-3 text-xs">
          <Badge
            variant="outline"
            className={`border-white/20 rounded-full ${
              connState === "connected" ? "text-[#7FE5C4]" :
              connState === "reconnecting" ? "text-[#FAB28A]" :
              connState === "connecting" ? "text-[#A9C0F5]" :
              "text-white/70"
            }`}
            data-testid="perito-conn-badge"
          >
            {connState === "reconnecting" ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> :
             (connState === "connected" ? <Wifi className="h-3 w-3 mr-1" /> : <WifiOff className="h-3 w-3 mr-1" />)}
            {connState === "connected" ? "Conectado" :
             connState === "connecting" ? "Conectando…" :
             connState === "reconnecting" ? "Reconectando…" :
             "Esperando"}
          </Badge>
          <Badge variant="outline" className={`border-white/20 rounded-full ${aseguradoPresent ? "text-[#7FE5C4]" : "text-[#FAB28A]"}`}>
            <Users className="h-3 w-3 mr-1" /> {aseguradoPresent ? "Asegurado en línea" : "Asegurado fuera"}
          </Badge>
          <Button size="sm" variant="outline" onClick={copyLink} className="bg-transparent border-white/20 text-white hover:bg-white/10 rounded-[10px]" data-testid="copy-link-call">
            <Copy className="h-3 w-3 mr-1" /> Enlace
          </Button>
        </div>
      </header>

      {/* Briefing del caso: lo que Ekspertium / el formulario de "Nueva
          peritación" envió como circunstancias. Visible por defecto para que
          el perito tenga contexto inmediato; se puede colapsar pulsando la
          cabecera. Se oculta por completo si no hay descripción. */}
      {(siniestro?.descripcion || siniestro?.compania_name || siniestro?.policy_template_name) && (
        <div className="bg-[#1A1816] border-b border-white/10" data-testid="case-briefing">
          <button
            type="button"
            onClick={() => setBriefingOpen((v) => !v)}
            className="w-full flex items-center justify-between px-4 py-2 text-left hover:bg-white/5"
            data-testid="case-briefing-toggle"
          >
            <div className="flex items-center gap-2 text-[#FFCBB1]">
              <Info className="h-4 w-4" />
              <span className="text-[12px] font-medium tracking-wide uppercase">Circunstancias del caso</span>
              {siniestro?.compania_name && (
                <span className="text-[11px] text-white/55 font-normal normal-case">· {siniestro.compania_name}</span>
              )}
              {siniestro?.policy_template_name && (
                <span className="text-[11px] text-white/55 font-normal normal-case">· {siniestro.policy_template_name}</span>
              )}
            </div>
            {briefingOpen ? <ChevronUp className="h-4 w-4 text-white/55" /> : <ChevronDown className="h-4 w-4 text-white/55" />}
          </button>
          {briefingOpen && (
            <div className="px-4 pb-3 -mt-1 text-[13px] text-white/85 leading-relaxed whitespace-pre-wrap break-words max-h-48 overflow-y-auto" data-testid="case-briefing-text">
              {siniestro.descripcion ? siniestro.descripcion : (
                <span className="italic text-white/55">Sin descripción de circunstancias. Pídele al asegurado que te cuente qué ocurrió.</span>
              )}
            </div>
          )}
        </div>
      )}

      {/* Main grid */}
      <div className="flex-1 grid grid-cols-12 gap-3 p-3 min-h-0">
        {/* Video area */}
        <div className="col-span-12 lg:col-span-8 flex flex-col gap-3 min-h-0">
          <div className="relative flex-1 bg-black rounded-[14px] overflow-hidden border border-white/10">
            <video ref={remoteVideoRef} autoPlay playsInline className="w-full h-full object-contain bg-black" data-testid="remote-video" />
            {!aseguradoPresent && (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
                <Loader2 className="h-8 w-8 animate-spin text-[#D85A30] mb-3" />
                <div className="font-heading font-semibold text-lg">Esperando a que entre {siniestro.asegurado_nombre.split(" ")[0]}…</div>
                <div className="text-sm text-white/70 mt-1 max-w-md">
                  Le hemos mandado un {siniestro.canal === "whatsapp" ? "WhatsApp" : "SMS"} con el enlace al {siniestro.asegurado_telefono}. ¿Tarda? Cópialo y se lo pasas tú.
                </div>
              </div>
            )}
            <div className="absolute top-3 left-3 flex items-center gap-2">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#D85A30] text-white text-[11px] font-medium">
                <span className="vp-live-dot" /> En directo
              </span>
            </div>
            {/* Aviso persistente: el asegurado tiene problemas de permisos */}
            {permissionIssue && (
              <div className="absolute top-12 left-3 right-3 max-w-md bg-[#B43F3F] border border-[#9A3535] rounded-[10px] p-3 shadow-lg z-30" data-testid="permission-issue-banner">
                <div className="flex items-start gap-2">
                  <div className="text-white text-[18px]">⚠️</div>
                  <div className="flex-1 text-white text-[12px] leading-snug">
                    <div className="font-semibold mb-0.5">El asegurado no puede continuar</div>
                    <div>
                      No ha podido activar:
                      {permissionIssue.camera_denied && <span className="ml-1 px-1.5 py-0.5 rounded bg-white/15 text-[11px]">cámara</span>}
                      {permissionIssue.microphone_denied && <span className="ml-1 px-1.5 py-0.5 rounded bg-white/15 text-[11px]">micrófono</span>}
                      {permissionIssue.geolocation_denied && <span className="ml-1 px-1.5 py-0.5 rounded bg-white/15 text-[11px]">ubicación</span>}
                    </div>
                    {permissionIssue.insured_phone ? (
                      <a href={`tel:${permissionIssue.insured_phone}`} className="inline-block mt-1.5 font-semibold underline" data-testid="permission-issue-call-link">
                        📞 Llamar al {permissionIssue.insured_phone}
                      </a>
                    ) : (
                      <div className="text-[11px] opacity-90 mt-1">Llámale por teléfono y guíale por los ajustes del navegador.</div>
                    )}
                    {permissionIssue.kind && (
                      <div className="text-[10px] opacity-70 mt-1 font-mono">{permissionIssue.kind}{permissionIssue.message ? ` · ${permissionIssue.message.slice(0, 80)}` : ""}</div>
                    )}
                  </div>
                  <button onClick={() => setPermissionIssue(null)} className="text-white/70 hover:text-white text-[16px] leading-none" data-testid="permission-issue-close" title="Cerrar aviso">×</button>
                </div>
              </div>
            )}
            {/* PIP local video */}
            <div className="absolute bottom-3 right-3 w-40 aspect-video rounded-[12px] overflow-hidden border border-white/30 bg-black/50">
              <video ref={localVideoRef} autoPlay playsInline muted className="w-full h-full object-cover" data-testid="local-video" />
              {!camOn && (
                <div className="absolute inset-0 flex items-center justify-center bg-[#2A2A2C]/95 text-[11px] text-white/70 text-center px-2">
                  Aún no enciende la cámara
                </div>
              )}
            </div>
          </div>

          {/* Controls */}
          <div className="flex flex-wrap items-center justify-center gap-2 bg-white/[0.04] border border-white/10 rounded-[14px] p-3">
            <Button onClick={toggleCam} disabled={!hasCamera} size="lg" className={`rounded-[12px] border ${camOn ? "bg-[#1D9E75] hover:bg-[#23B687] border-transparent text-white" : "bg-white/10 hover:bg-white/15 border-white/10 text-white"} disabled:opacity-40 disabled:cursor-not-allowed`} data-testid="toggle-cam-btn" title={camBtnTitle}>
              {camOn ? <Camera className="h-5 w-5 mr-2" /> : <CameraOff className="h-5 w-5 mr-2" />}
              {camBtnLabel}
            </Button>
            <Button onClick={toggleMic} size="lg" className={`rounded-[12px] border ${micOn ? "bg-[#1D9E75] hover:bg-[#23B687] border-transparent text-white" : "bg-white/10 hover:bg-white/15 border-white/10 text-white"}`} data-testid="toggle-mic-btn">
              {micOn ? <Mic className="h-5 w-5 mr-2" /> : <MicOff className="h-5 w-5 mr-2" />}
              {micOn ? "Micrófono" : "Silenciado"}
            </Button>
            <Button
              onClick={toggleInsuredTorch}
              disabled={!aseguradoPresent || !insuredTorchSupported}
              size="lg"
              className={`rounded-[12px] border ${insuredTorchOn ? "bg-[#F2C94C] hover:bg-[#E8B73A] border-transparent text-[#2C2C2A]" : "bg-white/10 hover:bg-white/15 border-white/10 text-white"} disabled:opacity-40 disabled:cursor-not-allowed`}
              data-testid="toggle-insured-torch-btn"
              title={
                !aseguradoPresent
                  ? "Esperando al asegurado"
                  : !insuredTorchSupported
                    ? "El dispositivo del asegurado no admite linterna"
                    : (insuredTorchOn ? "Apagar linterna del asegurado" : "Encender linterna del asegurado")
              }
            >
              {insuredTorchOn ? <Flashlight className="h-5 w-5 mr-2" /> : <FlashlightOff className="h-5 w-5 mr-2" />}
              {insuredTorchOn ? "Linterna encendida" : "Linterna"}
            </Button>
            <Button onClick={capture} disabled={!aseguradoPresent || capturing} size="lg" className="btn-eks-primary rounded-[12px]" data-testid="capture-btn">
              {capturing ? <Loader2 className="h-5 w-5 mr-2 animate-spin" /> : <ImagePlus className="h-5 w-5 mr-2" />}
              Capturar foto
            </Button>
            <Button onClick={endAndFinalize} disabled={finalizing} size="lg" className="btn-eks-danger rounded-[12px]" data-testid="finalize-call-btn">
              {finalizing ? <Loader2 className="h-5 w-5 mr-2 animate-spin" /> : <PhoneOff className="h-5 w-5 mr-2" />}
              Finalizar y certificar
            </Button>
          </div>

          {/* Photo gallery */}
          <div className="bg-white/[0.04] border border-white/10 rounded-[14px] p-3 min-h-[120px]">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-medium text-white/85 flex items-center gap-2">
                <ImagePlus className="h-3.5 w-3.5 text-[#D85A30]" /> Fotografías · {photos.length}
              </div>
              <div className="text-[11px] text-white/55">Cada foto guarda hora y ubicación</div>
            </div>
            {photos.length === 0 ? (
              <div className="text-xs text-white/55 py-6 text-center">Las fotos que captures aparecen aquí, con hora y ubicación.</div>
            ) : (
              <div className="flex gap-2 overflow-x-auto vp-scroll pb-1">
                {photos.map((p) => (
                  <a key={p.id} href={p.url} target="_blank" rel="noreferrer" className="shrink-0 vp-fade-in">
                    <img src={p.url} alt="captura" className="h-24 w-32 object-cover rounded-[12px] border border-white/20" />
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right panel: tabs (Chat / Transcripción / Notas IA / Causas) */}
        <div className="col-span-12 lg:col-span-4 flex flex-col min-h-0">
          <Tabs defaultValue="chat" className="flex flex-col flex-1 min-h-0">
            <TabsList className="bg-white/[0.06] border border-white/10 rounded-[12px] p-1 grid grid-cols-4 h-auto shrink-0">
              <TabsTrigger value="chat" className="rounded-[8px] data-[state=active]:bg-white data-[state=active]:text-[#D85A30] text-white/70 text-xs gap-1 py-1.5" data-testid="tab-chat">
                <MessageSquare className="h-3 w-3" /> Chat
              </TabsTrigger>
              <TabsTrigger value="trans" className="rounded-[8px] data-[state=active]:bg-white data-[state=active]:text-[#D85A30] text-white/70 text-xs gap-1 py-1.5" data-testid="tab-trans">
                <Sparkles className="h-3 w-3" /> Transcripción
              </TabsTrigger>
              <TabsTrigger value="ai" className="rounded-[8px] data-[state=active]:bg-white data-[state=active]:text-[#D85A30] text-white/70 text-xs gap-1 py-1.5" data-testid="tab-ai">
                <Brain className="h-3 w-3" /> Notas IA
              </TabsTrigger>
              <TabsTrigger value="causas" className="rounded-[8px] data-[state=active]:bg-white data-[state=active]:text-[#D85A30] text-white/70 text-xs gap-1 py-1.5" data-testid="tab-causas">
                <Sparkles className="h-3 w-3" /> Causas
              </TabsTrigger>
            </TabsList>

            <TabsContent value="chat" className="flex-1 min-h-0 mt-2">
              <div className="bg-white/[0.04] border border-white/10 rounded-[14px] flex flex-col h-full">
                <div className="px-4 py-3 border-b border-white/10 text-sm font-medium text-white/85 flex items-center gap-2">
                  <MessageSquare className="h-3.5 w-3.5 text-[#D85A30]" /> Chat con el asegurado
                </div>
                <div className="flex-1 overflow-y-auto vp-scroll p-3 space-y-2 min-h-0">
                  {chat.length === 0 ? (
                    <div className="text-xs text-white/55 text-center py-6">Cuando escriba, aparecerá aquí.</div>
                  ) : chat.map((m, i) => (
                    <div key={m.id || i} className={`flex ${m.role === "perito" ? "justify-end" : "justify-start"}`}>
                      <div className={`px-3 py-1.5 rounded-[12px] max-w-[80%] text-sm ${m.role === "perito" ? "bg-[#D85A30] text-white" : "bg-white/10 text-white"}`}>
                        {m.text}
                        <div className="text-[10px] text-white/60 mt-0.5">{fmtTime(m.created_at)}</div>
                      </div>
                    </div>
                  ))}
                  <div ref={chatEndRef} />
                </div>
                <div className="p-2 border-t border-white/10 flex gap-2">
                  <Input
                    value={chatInput} onChange={(e) => setChatInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && sendChat()}
                    placeholder="Escribe un mensaje…"
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/40 rounded-[10px]"
                    data-testid="chat-input"
                  />
                  <Button onClick={sendChat} className="btn-eks-primary rounded-[10px]" data-testid="chat-send-btn"><Send className="h-4 w-4" /></Button>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="trans" className="flex-1 min-h-0 mt-2">
              <div className="bg-white/[0.04] border border-white/10 rounded-[14px] flex flex-col h-full">
                <div className="px-4 py-3 border-b border-white/10 text-sm font-medium text-white/85 flex items-center gap-2">
                  <Sparkles className="h-3.5 w-3.5 text-[#D85A30]" /> Transcripción · español
                </div>
                <div className="flex-1 overflow-y-auto vp-scroll p-3 space-y-2 text-sm min-h-0">
                  {transcript.length === 0 ? (
                    <div className="text-xs text-white/55 text-center py-4">A medida que se hable, la conversación se transcribirá aquí.</div>
                  ) : transcript.map((t, i) => (
                    <div key={t.id || i} className="vp-fade-in flex gap-2">
                      <span className="text-[10px] text-white/45 font-mono shrink-0">{fmtTime(t.created_at)}</span>
                      <span className={`text-[11px] font-medium shrink-0 ${t.role === "perito" ? "text-[#D85A30]" : "text-[#5BCBA0]"}`}>
                        {t.role === "perito" ? "Perito" : "Asegurado"}:
                      </span>
                      <span className="text-white/90">{t.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="ai" className="flex-1 min-h-0 mt-2">
              <AiNotesPanel
                notes={aiNotes}
                loading={aiLoading}
                updatedAt={aiNotesAt}
                onRefresh={() => refreshAiNotes(true)}
                fullHeight
                onEditField={async (key, value) => {
                  try {
                    const r = await api.put(`/siniestros/${id}/ai-notes/${key}`, { value });
                    setAiNotes(r.data?.notes || null);
                    setAiNotesAt(r.data?.updated_at || new Date().toISOString());
                  } catch (e) {
                    toast.error("No se pudo guardar el cambio");
                  }
                }}
              />
            </TabsContent>

            <TabsContent value="causas" className="flex-1 min-h-0 mt-2">
              <AiNarrativePanel
                narrative={aiNarrative}
                edited={aiNarrativeEdited}
                fullHeight
                onSave={async (text) => {
                  try {
                    const r = await api.put(`/siniestros/${id}/ai-narrative`, { value: text });
                    setAiNarrative(r.data?.narrative || "");
                    setAiNarrativeEdited(!!r.data?.narrative_edited);
                    toast.success("Texto guardado para Ekspertium");
                  } catch (e) {
                    toast.error("No se pudo guardar el texto");
                  }
                }}
                onRegenerate={async () => {
                  try {
                    await api.put(`/siniestros/${id}/ai-narrative`, { value: null });
                    await refreshAiNotes(true);
                  } catch (e) {
                    toast.error("No se pudo regenerar");
                  }
                }}
              />
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
